import { useState, useEffect } from 'react'
import { useAuth } from '../lib/auth'
import { ArrowLeft, Key, Eye, EyeOff, Loader2, Copy, Check } from 'lucide-react'
import { Link } from 'react-router-dom'

export default function ResidentAccess() {
  const { token } = useAuth()
  const [pinCode, setPinCode] = useState<string | null>(null)
  const [showPin, setShowPin] = useState(false)
  const [loading, setLoading] = useState(true)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    fetchProfile()
  }, [])

  async function fetchProfile() {
    try {
      const res = await fetch('/api/auth/resident/profile', {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setPinCode(data.tenant?.pin_code || null)
      }
    } catch (err) {
      console.error('Failed to load profile', err)
    } finally {
      setLoading(false)
    }
  }

  function handleCopy() {
    if (pinCode) {
      navigator.clipboard.writeText(pinCode)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/" className="text-gray-500 hover:text-gray-700">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-lg font-semibold text-gray-900">Door Access</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* PIN Code Card */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Key className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Your PIN Code</h2>
              <p className="text-sm text-gray-500">Use this PIN to access doors and gates</p>
            </div>
          </div>

          {pinCode ? (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-5 py-4">
                  <p className="text-3xl font-mono font-bold text-gray-900 tracking-widest text-center">
                    {showPin ? pinCode : pinCode.replace(/./g, '\u2022')}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowPin(!showPin)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  {showPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  {showPin ? 'Hide PIN' : 'Show PIN'}
                </button>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copied!' : 'Copy PIN'}
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-gray-500">No PIN code assigned. Contact your property manager.</p>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-blue-800 mb-2">How to use your PIN</h3>
          <ul className="text-sm text-blue-700 space-y-1.5">
            <li>Enter your PIN on the keypad at any door or gate</li>
            <li>Your PIN is unique to you - do not share it</li>
            <li>If your PIN is not working, contact your property manager</li>
          </ul>
        </div>
      </main>
    </div>
  )
}
